import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Button,
  ButtonProps,
  CircularProgress,
  Link,
  TextField,
  Theme,
  Typography,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import Description from '@mui/icons-material/Description';
import Email from '@mui/icons-material/Email';
import LinkedIn from '@mui/icons-material/LinkedIn';
import Phone from '@mui/icons-material/Phone';
import classNames from 'classnames';
import { composeUsername } from '../../../lib/DisplayName';
import { formatPhoneNumber } from '../../../lib/PhoneNumber';
import { formatUrl } from '../../../lib/String';
import {
  RoomCollaboratorSchema,
  UserSchema,
} from '../../../definitions/schemas';
import { useI18n } from '../../../lib/hook/useI18n';
import { USER_ROLE_MAX_LENGTH } from '../../../enums/Constraints';
import ProfileEditor from '../../ProfileEditor';
import RolesEnum from '../../../enums/Roles';
import RoomOwner from '../../Rooms/Save/Steps/Review/RoomOwner';
import useDebouncedValue from '../../../lib/hook/useDebounceValue';
import UserStore, {
  UserRolesStore,
} from '../../../stores/observables/UserStore';

const useStyles = makeStyles((theme: Theme) => ({
  avatar: {
    backgroundColor: `${theme.palette.secondary.main}26`,
    color: theme.palette.secondary.main,
    height: theme.spacing(6),
    width: theme.spacing(6),
  },
  basicInformation: {
    [theme.breakpoints.only('md')]: {
      flexDirection: 'column',
    },
  },
  cardContainer: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    height: '100%',
    margin: 'auto',
    padding: theme.spacing(2, 2, 1, 2),
    position: 'relative',
  },
  corner: {
    background: theme.palette.secondary.main,
    borderRight: `${theme.spacing(3)} solid ${theme.palette.background.paper}`,
    borderTopLeftRadius: theme.spacing(1),
    borderTop: `${theme.spacing(3)} solid ${theme.palette.secondary.main}`,
    bottom: -1,
    height: theme.spacing(3),
    position: 'absolute',
    right: -1,
    width: theme.spacing(3),
  },
  disabledAvatar: {
    background: 'var(--gray-light)',
    color: theme.palette.text.disabled,
    opacity: 0.5,
  },
  disabledText: {
    color: 'var(--gray-lighter)',
  },
  editRoleDescription: {
    '& .MuiFormHelperText-root': {
      margin: 0,
      textAlign: 'right',
    },
  },
  ellipsis: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  informationData: {
    ...theme.typography.body2,
    color: 'var(--divider-color-light)',
    wordBreak: 'break-word',
  },
  informationIcon: {
    color: 'var(--divider-color-light)',
  },
  informationUnavailable: {
    color: 'var(--gray-lighter)',
  },
  linkedin: {
    cursor: 'pointer',
  },
  name: {
    wordBreak: 'break-word',
  },
  nameWrapper: {
    marginLeft: theme.spacing(2),
    marginRight: theme.spacing(1),
    [theme.breakpoints.only('md')]: {
      margin: 0,
      marginTop: theme.spacing(2),
    },
  },
}));

export interface BussinessCardProps extends Partial<RoomCollaboratorSchema> {
  cardBorder?: boolean;
  collaborator?: RoomCollaboratorSchema;
  disabled?: boolean;
  editable?: boolean;
  editProfileButtonProps?: ButtonProps;
  enableBody?: boolean;
  enableChangeRoomOwner?: boolean;
  enableHeader?: boolean;
  hideEmail?: boolean;
  hideRoleDescription?: boolean;
  onRoomOwnerChange?: (value: UserSchema) => void;
  onSaveSuccess?: () => void;
  onUpdateProfile?: (user: UserSchema) => void;
  onUpdateRoleDescription?: (roleDescription: string) => void;
  owner?: UserSchema;
  readOnly?: boolean;
}

const BussinessCard: React.FC<BussinessCardProps> = (
  props: BussinessCardProps
): JSX.Element => {
  const { t } = useI18n();
  const classes = useStyles();

  // if owner is present, use owner values as default
  // if owner isn't present, use prop values(from RoomCollaboratorSchema) as default
  // if owner and prop values are present, use prop values(from RoomCollaboratorSchema) as default
  const {
    owner: initialOwner,
    cardBorder = false,
    collaborator,
    disabled = false,
    editable,
    editProfileButtonProps,
    email = initialOwner?.email,
    enableBody = false,
    enableChangeRoomOwner = false,
    enableHeader = false,
    username = composeUsername(initialOwner),
    hideEmail = false,
    hideRoleDescription = false,
    jobTitle = initialOwner?.member?.jobTitle,
    linkedin = initialOwner?.linkedin,
    onRoomOwnerChange,
    onUpdateRoleDescription,
    phoneNumber = initialOwner?.phoneNumber,
    photo = initialOwner?.photo,
    readOnly = false,
    roleDescription: initialRoleDescription = initialOwner?.member
      ?.roleDescription,
    onSaveSuccess,
    onUpdateProfile,
  } = props;

  const [editProfile, setEditProfile] = useState(false);
  const [owner, setOwner] = useState(initialOwner);
  const [loading, setLoading] = useState(false);
  const [readyToEdit, setReadyToEdit] = useState(false);
  const [roleDescription, setRoleDescription] = useState(
    initialRoleDescription
  );
  const debouncedRoleDescriptionValue = useDebouncedValue(
    roleDescription || '',
    2000
  );

  const currentUser = UserStore.get();
  const isCurrentUser = collaborator?.collaboratorId === currentUser?.id;

  const currentRole = UserRolesStore.getCurrent()?.role;
  const isAdmin = currentRole?.name === RolesEnum.ADMIN;

  useEffect(() => {
    if (initialOwner) {
      setOwner(initialOwner);
      const roleDesc = initialOwner?.member?.roleDescription;
      setRoleDescription((prev) => prev || roleDesc);
      setReadyToEdit(false);
    }
  }, [initialOwner]);

  useEffect(() => {
    setRoleDescription(initialRoleDescription);
    setReadyToEdit(false);
  }, [initialRoleDescription]);

  useEffect(() => {
    if (loading) {
      setLoading(false);
    }
  }, [loading]);

  // autosave role description on debouncedRoleDescriptionValue
  // added readyToEdit flag to avoid trigger autosave on setRoleDescription initial Value
  useEffect(() => {
    if (
      readyToEdit &&
      editable &&
      onUpdateRoleDescription &&
      debouncedRoleDescriptionValue !== null
    ) {
      onUpdateRoleDescription(debouncedRoleDescriptionValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedRoleDescriptionValue, readyToEdit]);

  const toggleEditProfile = () => {
    setEditProfile(!editProfile);
  };

  const onProfileUpdated = (user: UserSchema) => {
    if (onUpdateProfile) {
      onUpdateProfile(user);
    }
    if (onSaveSuccess) {
      onSaveSuccess();
    }

    // force to reload RoomOwner component
    setLoading(true);
  };

  const onRoleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRoleDescription(e.target.value);
    setReadyToEdit(true);
  };

  const renderAvatar = () => (
    <Avatar
      classes={{
        root: classes.avatar,
      }}
      className={classNames({ [classes.disabledAvatar]: disabled })}
      src={photo}
    >
      {(username || '-')[0].toUpperCase()}
    </Avatar>
  );

  const renderLinkendin = () => {
    if (readOnly && !linkedin) {
      return null;
    }

    const classNameUnavailable = {
      [classes.informationUnavailable]: !linkedin || disabled,
    };

    return (
      <Box alignItems="center" display="flex" mb={2} width="100%">
        <LinkedIn
          className={classNames(classes.informationIcon, classNameUnavailable)}
        />
        <Box
          className={classNames(
            classes.informationData,
            classes.ellipsis,
            classNameUnavailable
          )}
          ml={1.5}
        >
          {linkedin ? (
            <Link
              color="secondary"
              href={formatUrl(linkedin)}
              target="_blank"
              title={linkedin}
              underline="hover"
            >
              {t('business_card.linkedin.view_profile')}
            </Link>
          ) : (
            'No LinkedIn Profile URL'
          )}
        </Box>
      </Box>
    );
  };

  const renderJobTitle = () => {
    if (readOnly && !jobTitle) {
      return null;
    }

    const classNameUnavailable = {
      [classes.informationUnavailable]: !jobTitle || disabled,
    };

    return (
      <Box
        className={classNames(classes.informationData, classNameUnavailable)}
        mt={0.5}
      >
        {jobTitle || 'No job title'}
      </Box>
    );
  };

  const renderPhone = () => {
    if (readOnly && !phoneNumber) {
      return null;
    }

    const classNameUnavailable = {
      [classes.informationUnavailable]: !phoneNumber || disabled,
    };

    return (
      <Box alignItems="center" display="flex" mb={2} width="100%">
        <Phone
          className={classNames(classes.informationIcon, classNameUnavailable)}
        />
        <Box
          className={classNames(classes.informationData, classNameUnavailable)}
          ml={1.5}
        >
          {formatPhoneNumber(phoneNumber) || 'No phone number'}
        </Box>
      </Box>
    );
  };

  const renderEmail = () => {
    if (hideEmail || (readOnly && !email)) {
      return null;
    }

    const classNameUnavailable = {
      [classes.informationUnavailable]: email || disabled,
    };

    return (
      <Box alignItems="center" display="flex" mb={2} width="100%">
        <Email
          className={classNames(classes.informationIcon, classNameUnavailable)}
        />
        <Box
          className={classNames(classes.informationData, classNameUnavailable)}
          ml={1.5}
        >
          {email}
        </Box>
      </Box>
    );
  };

  const renderRoleDescription = () => {
    if ((readOnly && !roleDescription) || hideRoleDescription) {
      return null;
    }

    let content = <>{roleDescription || 'No role description'}</>;

    const classNameUnavailable = {
      [classes.informationUnavailable]: !roleDescription || disabled,
    };

    if (editable && onUpdateRoleDescription && !readOnly) {
      const roleLength = roleDescription?.length || 0;

      content = (
        <TextField
          className={classes.editRoleDescription}
          color="secondary"
          fullWidth
          helperText={`${roleLength}/${USER_ROLE_MAX_LENGTH}`}
          inputProps={{ maxLength: USER_ROLE_MAX_LENGTH }}
          multiline
          onChange={onRoleDescriptionChange}
          placeholder="Role description"
          size="small"
          value={roleDescription || ''}
          variant="outlined"
        />
      );
    }

    return (
      <Box alignItems="flex-start" display="flex" mb={2} width="100%">
        <Description
          className={classNames(classes.informationIcon, classNameUnavailable)}
        />
        <Box
          className={classNames(classes.informationData, classNameUnavailable)}
          ml={1.5}
          width="100%"
        >
          {content}
        </Box>
      </Box>
    );
  };

  if (loading) {
    return <CircularProgress color="secondary" size={32} />;
  }

  return (
    <Box
      className={classNames({ [classes.cardContainer]: cardBorder })}
      width="100%"
    >
      <div className={classNames({ [classes.corner]: cardBorder })} />
      {enableChangeRoomOwner && owner && (
        <Box mb={2} width="100%">
          <RoomOwner onRoomOwnerChange={onRoomOwnerChange} owner={owner} />
        </Box>
      )}

      {enableHeader && (
        <Box
          className={classes.basicInformation}
          display="flex"
          position="relative"
          width="100%"
        >
          {renderAvatar()}
          <Box
            className={classes.nameWrapper}
            display="flex"
            flexDirection="column"
            justifyContent="center"
          >
            <Box className={classes.name}>
              <Typography
                className={classNames({ [classes.disabledText]: disabled })}
                variant="body1"
              >
                {username}
              </Typography>
            </Box>
            {renderJobTitle()}
          </Box>
        </Box>
      )}
      {enableBody && (
        <Box mt={enableHeader ? 3 : 0}>
          {renderLinkendin()}
          {renderPhone()}
          {renderEmail()}
          {renderRoleDescription()}
          {!readOnly && (isCurrentUser || isAdmin) && (
            <Box display="flex" justifyContent="flex-start" my={2}>
              <Button
                {...editProfileButtonProps}
                color="secondary"
                onClick={toggleEditProfile}
                variant="outlined"
              >
                Edit Profile
              </Button>
              {editProfile && collaborator && (
                <ProfileEditor
                  onClose={toggleEditProfile}
                  onSave={onProfileUpdated}
                  open={editProfile}
                  user={{
                    ...collaborator,
                    id: collaborator.collaboratorId,
                    email: collaborator.email!,
                    member: {
                      id: '',
                      jobTitle: collaborator.jobTitle,
                    },
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default BussinessCard;
